import { Grid } from '@atlaskit/primitives/grid';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import { useDisableChoreographer, useInitialData } from '@post-office/placement-contexts/hooks';

import { TemplateCardLoader } from './TemplateCardLoader';

import { MessageRenderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { withProductStoreTemplateRecommendationsIntlProvider } from '../__generated__/intl-provider';

import { type ProductStoreTemplateRecommendationsInitialData } from '../api/types';

import { type Placement as ProductStoreTemplateRecommendationsPlacement } from '../contracts/Placement';

import { type OnPerformanceTrackingEvent } from '@post-office/placement-contexts/create-placement-component/hooks/usePerformanceTracking/types';

import {
	type ContextWithRoute,
	type FallbackRenderFacade,
} from '@post-office/placement-contexts/create-placement-component/types';

import { type ComponentType } from 'react';

import { type ErrorBoundaryPropsWithRender } from 'react-error-boundary';

const LoadingComponent = () => (
	<Grid
		gap="space.300"
		templateColumns="repeat(auto-fit, minmax(342px, 1fr))"
		testId="product-store-template-recommendations"
	>
		<TemplateCardLoader />
		<TemplateCardLoader />
		<TemplateCardLoader />
	</Grid>
);

export const ProductStoreTemplateRecommendationsPlacementComponent: ComponentType<
	Partial<ErrorBoundaryPropsWithRender> & {
		context: ContextWithRoute;
		initialData: Promise<Response | undefined> | undefined;
		onEvent?: OnPerformanceTrackingEvent;
	} & ProductStoreTemplateRecommendationsPlacement &
		FallbackRenderFacade & {
			messages?: {
				[x: string]: string;
			};
		}
> = withProductStoreTemplateRecommendationsIntlProvider(
	createPlacementComponent<ProductStoreTemplateRecommendationsPlacement>(
		PLACEMENT_ID,
		({ initialData, onTryNow, onLoadComplete }) => {
			// Disable choreographer integration as this placement renders multiple messages at a time.
			// https://developer.atlassian.com/platform/post-office/delivery-channels-and-placements/in-app/integrating-post-office-with-a-product/#choreographer-api
			useDisableChoreographer();

			const { data, isLoading, error } =
				useInitialData<ProductStoreTemplateRecommendationsInitialData>(initialData);

			if (error) {
				// Will be caught by "create-placement-component" error boundary
				throw error;
			}

			if (isLoading) {
				return <LoadingComponent />;
			}

			onLoadComplete(
				data.messages.map(({ productKey, templateKey }) => ({ productKey, templateKey })),
			);

			return (
				<Grid
					gap="space.300"
					testId="product-store-template-recommendations"
					templateColumns="repeat(auto-fit, minmax(342px, 1fr))"
				>
					{data.messages.map((message, index) => (
						<MessageRenderer
							key={index}
							{...message}
							onTryNow={onTryNow}
							loadingFallback={<TemplateCardLoader />}
						/>
					))}
				</Grid>
			);
		},
	),
);

export default ProductStoreTemplateRecommendationsPlacementComponent;
